import { useCallback, useSyncExternalStore } from 'react';
import type { CustomerLocale } from '@capsico/contracts';
import { en, type MessageKey, type Messages } from './en';
import { hi } from './hi';
import { ne } from './ne';

const DICTIONARIES: Record<CustomerLocale, Messages> = { en, hi, ne };
const KEY = 'capsico:customer:locale';

export const LOCALE_NAMES: Record<CustomerLocale, string> = {
  en: 'English',
  hi: 'हिन्दी',
  ne: 'नेपाली',
};

function initial(): CustomerLocale {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored === 'en' || stored === 'hi' || stored === 'ne') return stored;
  } catch {
    // ignore
  }
  return 'en';
}

let current: CustomerLocale = typeof window === 'undefined' ? 'en' : initial();
const listeners = new Set<() => void>();

/** Switches the app language now and remembers it on this device. */
export function setLocale(locale: CustomerLocale): void {
  if (locale === current) return;
  current = locale;
  try {
    window.localStorage.setItem(KEY, locale);
  } catch {
    // ignore
  }
  document.documentElement.lang = locale;
  for (const listener of listeners) listener();
}

export function getLocale(): CustomerLocale {
  return current;
}

/** Fills {name} placeholders. Unknown placeholders are left as they are. */
export function format(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** The current language and a translator bound to it. */
export function useI18n(): { locale: CustomerLocale; t: Translate; setLocale: typeof setLocale } {
  const locale = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => 'en' as CustomerLocale,
  );
  const t = useCallback<Translate>(
    (key, vars) => format(DICTIONARIES[locale][key] ?? en[key], vars),
    [locale],
  );
  return { locale, t, setLocale };
}

export type { MessageKey };
